// MAIN-861 group orders: pure helpers. Kept in .js so Jest can test them.
import moment from "moment";

export const GROUP_STASH_KEY = "cp.groupOrder.personalDraftStash";

const round2 = (n) => Math.round(n * 100) / 100;
const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// The API's budget figures for a group order. The API prices the cart with every
// fee, tax and tip on each save, so the draft's own lines can't be used here.
// estimatedTotal is null when it couldn't be priced; the food subtotal stands in.
export const serverBudget = (go) => {
  const overBudgetBy = toNumber(go?.overBudgetBy);
  return {
    subtotal: toNumber(go?.subtotal),
    estimatedTotal:
      go?.estimatedTotal === null || go?.estimatedTotal === undefined
        ? null
        : toNumber(go.estimatedTotal),
    totalBudget: toNumber(go?.totalBudget),
    perPersonTotal: toNumber(go?.perPersonTotal),
    overBudgetBy,
    overBudgetReason: go?.overBudgetReason || null,
    isOver: overBudgetBy > 0,
  };
};

// The limit that applies: the total, or the per-person amount × headcount when
// that is lower. The over-budget amount is measured against the same limit.
export const budgetLimit = (budget) => {
  const total = toNumber(budget?.totalBudget);
  const perPerson = toNumber(budget?.perPersonTotal);
  if (perPerson > 0 && (!total || perPerson < total)) return perPerson;
  return total;
};

export const budgetLabel = (budget, format) => {
  const priced = budget.estimatedTotal !== null && budget.estimatedTotal !== undefined;
  const spent = priced ? budget.estimatedTotal : budget.subtotal;
  const limit = budgetLimit(budget);
  if (!limit) return `${format(spent)} ${priced ? "estimated total" : "subtotal"}`;
  if (budget.isOver) {
    return `${format(budget.overBudgetBy)} over budget (${format(spent)} of ${format(limit)})`;
  }
  return `${format(spent)} of ${format(limit)} used`;
};

export const perPersonHint = (go, format) =>
  toNumber(go?.perPersonBudget) > 0
    ? `${format(toNumber(go.perPersonBudget))}/person × ${toNumber(go.headCount) || toNumber(go.maxHeadCount)}`
    : null;

export const parseCart = (groupOrder) => {
  try {
    const parsed = JSON.parse(groupOrder?.cartDetailsJson || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const OPTIONAL_DRAFT_FIELDS = ["driverTip", "driverTipType", "tipPercentage", "tablewareDeclined"];

export const buildDraftFromGroupOrder = (go, { mode, repCustomerId }) => {
  const customerId = mode === "rep" ? repCustomerId || "" : "";
  const draft = {
    groupOrderId: go._id,
    customerId,
    catererId: go.catererId || "",
    catererSlug: go.catererSlug || "",
    catererName: go.catererName || "",
    cartDetails: parseCart(go).map((line) => (mode === "rep" ? { ...line, customerId } : line)),
    street: go.street || "",
    location: go.street || "",
    locationName: go.locationName || "",
    placeId: go.placeId || "",
    suit: go.suit || "",
    city: go.city || "",
    zip: go.zip || "",
    lat: go.lat,
    lng: go.lng,
    state: go.state || "",
    headCount: go.headCount || go.maxHeadCount || 1,
    deliveryDate: go.deliveryDate || null,
    deliveryTime: go.deliveryTime || "",
    contactPerson: go.contactPerson || "",
    contactNo: go.contactNo || "",
    instruction: go.instruction || "",
  };
  // Never-set tip/tableware stay off the draft so the caterer's defaults apply.
  OPTIONAL_DRAFT_FIELDS.forEach((key) => {
    if (go[key] !== null && go[key] !== undefined) draft[key] = go[key];
  });
  return draft;
};

// Token saves don't return the rep's first name; keep the one already loaded.
export const mergeSaveResult = (previous, result) => ({
  ...result,
  repFirstName: result?.repFirstName || previous?.repFirstName || null,
});

// Group order form: a new street drops the old place details (coordinates, zip,
// city and so on) until the picked suggestion's details arrive. The same street
// keeps them, so prefilled edit values can be saved without retyping.
export const applyStreetChange = (form, value) => {
  const street = value || "";
  if (form.street === street) return form;
  return {
    ...form,
    street,
    zip: "",
    city: "",
    state: "",
    locationName: "",
    placeId: "",
    lat: null,
    lng: null,
  };
};

// localStorage, or null when the browser blocks it (reading it can throw).
export const browserStorage = () => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch (e) {
    return null;
  }
};

// Blocked or full storage only loses the stash; it never breaks the page.
export const stashPersonalDraft = (storage, draft) => {
  if (!storage || !draft || draft.groupOrderId) return;
  try {
    if (storage.getItem(GROUP_STASH_KEY) !== null) return;
    storage.setItem(GROUP_STASH_KEY, JSON.stringify(draft));
  } catch (e) {
    // storage unavailable: nothing to restore later
  }
};

export const takeStash = (storage) => {
  if (!storage) return null;
  let raw = null;
  try {
    raw = storage.getItem(GROUP_STASH_KEY);
    storage.removeItem(GROUP_STASH_KEY);
  } catch (e) {
    // storage unavailable: use whatever was read, if anything
  }
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

// /orderconfirmed is excluded because CheckoutSuccess restores the stash itself;
// the guard (a parent effect) would otherwise run after it and wipe the restored cart.
const GROUP_PATHS = [
  /^\/group\//,
  /^\/group-orders\/[^/]+\/caterers/,
  /^\/revieworder/,
  /^\/login$/,
  /^\/orderconfirmed/,
];

export const shouldRestorePersonalDraft = (pathname, draftGroupOrderId) =>
  !!draftGroupOrderId && !GROUP_PATHS.some((re) => re.test(pathname));

// Shows a stored phone the way the shared PhoneForm formats it, (555) 555-1234.
// A leading US country code is dropped. PhoneForm only accepts up to 10 digits,
// so anything longer (an old free-text number with an extension) starts empty
// instead of showing a value the field can't edit.
export const toPhoneDisplay = (phone) => {
  if (!phone) return "";
  let digits = String(phone).replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length > 10) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const shareUrl = (origin, token) => `${origin}/group/${token}`;

export const hasDeliveryDetails = (go) => !!go?.street && !!go?.deliveryDate;

// Router state for the caterer list/page, or null so the office uses the
// normal (empty) search.
export const buildSearchState = (go) =>
  hasDeliveryDetails(go)
    ? {
        address: go.street,
        date: new Date(go.deliveryDate),
        count: go.headCount || go.maxHeadCount,
        cordinates: [go.lng, go.lat],
        zipCode: go.zip,
        suit: go.suit,
        locationName: go.locationName,
        city: go.city,
      }
    : null;

const SAVED_FIELDS = [
  "catererId",
  "street",
  "suit",
  "city",
  "state",
  "zip",
  "locationName",
  "placeId",
  "lat",
  "lng",
  "deliveryDate",
  "headCount",
  "contactPerson",
  "contactNo",
  "instruction",
  "driverTip",
  "driverTipType",
  "tipPercentage",
  "tablewareDeclined",
];

export const groupSaveSnapshot = (draft) =>
  JSON.stringify({
    cartDetails: draft?.cartDetails || [],
    ...SAVED_FIELDS.reduce((acc, k) => ({ ...acc, [k]: draft?.[k] ?? null }), {}),
  });

const numOrNull = (v) => {
  const n = Number(v);
  return v === "" || v === null || v === undefined || !Number.isFinite(n) ? null : n;
};

// catererSlug/catererName are never sent: the API rejects them and derives the name itself.
export const buildSaveInput = (draft, version) => {
  const date = draft?.deliveryDate ? new Date(draft.deliveryDate) : null;
  const validDate = date && Number.isFinite(date.getTime()) ? date : null;
  const headCount = numOrNull(draft?.headCount);
  return {
    catererId: draft?.catererId || "",
    cartDetailsJson: JSON.stringify(draft?.cartDetails || []),
    version,
    street: draft?.street || "",
    suit: draft?.suit || "",
    city: draft?.city || "",
    state: draft?.state || "",
    zip: draft?.zip || "",
    locationName: draft?.locationName || "",
    placeId: draft?.placeId || "",
    lat: numOrNull(draft?.lat),
    lng: numOrNull(draft?.lng),
    deliveryDate: validDate ? validDate.toISOString() : null,
    deliveryTime: validDate ? moment(validDate).format("hh:mm A") : "",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    headCount: headCount && headCount >= 1 ? Math.round(headCount) : null,
    contactPerson: draft?.contactPerson || "",
    contactNo: draft?.contactNo || "",
    instruction: draft?.instruction || "",
    driverTip: numOrNull(draft?.driverTip),
    driverTipType: draft?.driverTipType || null,
    tipPercentage: numOrNull(draft?.tipPercentage),
    tablewareDeclined:
      typeof draft?.tablewareDeclined === "boolean" ? draft.tablewareDeclined : null,
  };
};

export const repStatusLabel = (go) => {
  switch (go?.status) {
    case "Ready":
      return "Ready for you";
    case "CheckingOut":
      return "Checking out";
    case "Placed":
      return "Placed";
    case "Cancelled":
      return "Cancelled";
    default:
      return go?.itemsUpdatedAt ? "Office is editing" : "Waiting on office";
  }
};

export const adminStatusLabel = (go) => {
  switch (go?.status) {
    case "Ready":
      return go.readyByKind === "admin" ? "Submitted by CaterPlace" : "Submitted by office";
    case "CheckingOut":
      return "Rep checking out";
    case "Placed":
      return "Placed";
    case "Cancelled":
      return "Cancelled";
    default:
      return go?.itemsUpdatedAt ? "Office adding items" : "Waiting on office";
  }
};

export const formatDelivery = (go) =>
  go?.deliveryDate ? moment(go.deliveryDate).format("ddd, MMM D · h:mm A") : "Not set yet";

export const isOfficeReadOnly = (go, now) =>
  go?.status !== "Open" ||
  (!!go?.deliveryDate && new Date(go.deliveryDate).getTime() < now.getTime());

export const MISSING_LABELS = {
  caterer: "pick a caterer",
  address: "enter the full delivery address",
  deliveryDate: "choose a delivery date and time",
  contactPerson: "add an on-site contact name",
  contactNo: "add an on-site contact phone",
  items: "add at least one item",
};
export const missingLabel = (code) => MISSING_LABELS[code] || code;
